/**
 * PublicHeader Component
 * Shared header for public-facing pages (Landing, Invest, HiveFund info)
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '../new-components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../new-components/ui/sheet';
import { BRAND_ASSETS, TENANT } from '../../config';
import { NavItem } from './navConfig';

interface PublicHeaderProps {
  navItems: NavItem[];
  activeSection?: string;
  onScrollToSection?: (id: string) => void;
}

export const PublicHeader = ({
  navItems,
  activeSection,
  onScrollToSection,
}: PublicHeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (item: NavItem): boolean => {
    if (item.scrollTo && activeSection) {
      return activeSection === item.scrollTo;
    }
    if (item.path) {
      return location.pathname === item.path;
    }
    return false;
  };

  const handleNavClick = (item: NavItem) => {
    if (item.scrollTo && onScrollToSection) {
      onScrollToSection(item.scrollTo);
    }
    setIsMenuOpen(false);
  };

  const renderNavItem = (item: NavItem, isMobile = false) => {
    const baseClasses = isMobile
      ? 'text-lg font-medium text-foreground hover:text-primary transition-colors text-left'
      : `py-2 text-base font-medium transition-all relative ${
          isActive(item)
            ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
            : 'text-muted-foreground hover:text-foreground'
        }`;

    // If item has scrollTo and we have a scroll handler, use button
    if (item.scrollTo && onScrollToSection) {
      return (
        <button
          key={item.label}
          onClick={() => handleNavClick(item)}
          className={baseClasses}
        >
          {item.label}
        </button>
      );
    }

    // Otherwise use Link
    if (item.path) {
      return (
        <Link
          key={item.label}
          to={item.path}
          onClick={() => setIsMenuOpen(false)}
          className={baseClasses}
        >
          {item.label}
        </Link>
      );
    }

    return null;
  };

  return (
    <header className="border-b border-border bg-secondary sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-start gap-8">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center transition duration-300 hover:opacity-80 flex-shrink-0"
          >
            <img
              src={BRAND_ASSETS.logo.primary}
              alt={`${TENANT.branding.name} Logo`}
              className="h-12 w-auto font-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => renderNavItem(item))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center gap-3 ml-auto">
            <Link to="/login">
              <Button
                variant="outline"
                className="font-semibold"
              >
                Log In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="font-semibold">Sign Up</Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden ml-auto">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <nav className="flex flex-col gap-6 mt-8">
                  {navItems.map((item) => renderNavItem(item, true))}
                  <Link to="/login" className="mt-4">
                    <Button
                      variant="outline"
                      className="w-full font-semibold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Log In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      className="w-full font-semibold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign Up
                    </Button>
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
